import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/NotificationBell';
import { Badge } from '@/components/ui/badge';
import { 
  LayoutDashboard, 
  ArrowRightLeft, 
  CreditCard, 
  Landmark, 
  Users, 
  LogOut,
  Menu,
  X,
  FileText,
  Package,
  ShoppingCart,
  Bell,
  Moon,
  Sun,
  Settings,
  UserCog,
  TreePine,
  TrendingUp,
  Truck,
  Factory,
  ClipboardList,
  MessageCircle,
  BookOpen,
  Bot,
  HelpCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Fechado por padrão no mobile
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Fechar sidebar ao mudar de página no mobile
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Verificar tamanho da tela ao carregar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/orders', label: 'Pedidos', icon: ShoppingCart },
    { path: '/ordens-servico', label: 'Ordens de Serviço', icon: ClipboardList },
    { path: '/entregas', label: 'Entregas', icon: Truck },
    { path: '/producao', label: 'Produção', icon: Factory },
    { path: '/products', label: 'Produtos', icon: Package },
    { path: '/estoque-madeira', label: 'Estoque Madeira', icon: TreePine },
    { path: '/customers', label: 'Clientes', icon: Users },
    { path: '/transactions', label: 'Financeiro', icon: ArrowRightLeft },
    { path: '/dre', label: 'DRE (Lucro)', icon: TrendingUp },
    { path: '/employees', label: 'Funcionários', icon: UserCog },
    { path: '/nfe', label: 'NF-e', icon: FileText },
    { path: '/simulador-cartao', label: 'Simulador Cartão', icon: CreditCard },
    { path: '/lembretes', label: 'Lembretes', icon: Bell },
  ];

  const centralItems = [
    { path: '/central/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-central-dashboard' },
    { path: '/central/conversas', label: 'Conversas', icon: MessageCircle, testId: 'nav-central-conversas' },
    { path: '/central/aguardando', label: 'Aguardando Atendimento', icon: HelpCircle, testId: 'nav-central-aguardando' },
    { path: '/central/base-conhecimento', label: 'Base de Conhecimento', icon: BookOpen, testId: 'nav-central-base-conhecimento' },
    { path: '/central/config-ia', label: 'Configurações da IA', icon: Bot, testId: 'nav-central-config-ia' },
    { path: '/central/config-whatsapp', label: 'Config. WhatsApp', icon: Settings, testId: 'nav-central-config-whatsapp' },
  ];

  if (isAdmin()) {
    navItems.push({ path: '/users', label: 'Usuários', icon: Users });
    navItems.push({ path: '/settings', label: 'Configurações', icon: Settings });
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header fixo */}
      <header 
        className="bg-background/80 backdrop-blur-md border-b border-border fixed top-0 left-0 right-0 z-50"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center justify-between px-3 sm:px-4 py-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="sidebar-toggle"
              className="h-9 w-9"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <h1 className="text-base sm:text-lg md:text-xl font-bold font-manrope text-primary truncate max-w-[150px] sm:max-w-none">Serraria Guedes</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="h-9 w-9"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/notifications')}
              className="relative h-9 w-9"
              data-testid="notifications-button"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <Badge 
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive"
                  data-testid="notification-badge"
                >
                  {unreadCount}
                </Badge>
              )}
            </Button>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.role === 'admin' ? 'Administrador' : 'Usuário'}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              className="h-9 w-9"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Spacer para compensar o header fixo */}
      <div style={{ height: 'calc(57px + env(safe-area-inset-top, 0px))' }} />

      <div className="flex">
        {/* Overlay para fechar sidebar no mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            style={{ top: 'calc(57px + env(safe-area-inset-top, 0px))' }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          } fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transition-transform duration-200 ease-in-out`}
          style={{ top: 'calc(57px + env(safe-area-inset-top, 0px))' }}
        >
          <nav className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 57px - env(safe-area-inset-top, 0px))' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                >
                  <div
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-[0_0_10px_rgba(52,211,153,0.2)]'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                </Link>
              );
            })}

            {/* Seção — Central de Atendimento */}
            <div className="pt-4 pb-1 px-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-t border-border/60 mt-3">
              Central de Atendimento
            </div>
            {centralItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={item.testId}
                  onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                >
                  <div
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-[0_0_10px_rgba(52,211,153,0.2)]'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-4 md:p-8 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
};